import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../services/api";
import { showToast } from "../helper/notifications"; // UPDATED IMPORT
import {
  GoogleMap,
  Autocomplete
} from "@react-google-maps/api";
import { useGoogleMaps } from "../context/GoogleMapsContext"; 
import DrawTools from "../components/DrawTools";
import PageHeader from "../components/common/PageHeader";
import { Icon } from "@iconify/react";

/* ================= THEME CONSTANTS ================= */
const MAP_CONTAINER_STYLE = { width: "100%", height: "550px", borderRadius: "12px" };
const DEFAULT_CENTER = { lat: 48.137, lng: 11.575 }; 

const LocationsTopFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isLoaded } = useGoogleMaps();

  const mapRef = useRef(null);
  const autocompleteRef = useRef(null);

  const [drawVersion, setDrawVersion] = useState(0);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    geometrics_outline: ""
  });

  // 1. Logic to focus map on existing boundaries
  const fitMapToBoundary = useCallback((outlineData) => {
    if (!outlineData || !mapRef.current) return;
    try {
      const parsed = typeof outlineData === 'string' ? JSON.parse(outlineData) : outlineData;
      if (!Array.isArray(parsed)) return;

      const bounds = new window.google.maps.LatLngBounds();
      let hasCoords = false;

      parsed.forEach(item => {
        const path = Array.isArray(item) ? item : item.path;
        if (Array.isArray(path)) {
          path.forEach(pt => {
            if (pt.lat && pt.lng) {
              bounds.extend({ lat: parseFloat(pt.lat), lng: parseFloat(pt.lng) });
              hasCoords = true;
            }
          });
        }
      });

      if (hasCoords) {
        mapRef.current.fitBounds(bounds, 50); 
      }
    } catch (e) {
      console.warn("Boundary zoom failed:", e);
    }
  }, []);

  // 2. Fetch data if editing
  useEffect(() => {
    if (id && isLoaded) loadData();
  }, [id, isLoaded]);

  const loadData = async () => {
    try {
      const res = await api.get(`/top-locations/${id}`);
      const data = res.data?.user || res.data; 
      
      const outline = typeof data.geometrics_outline === 'object' 
        ? JSON.stringify(data.geometrics_outline) 
        : data.geometrics_outline;

      setFormData({
        name: data.name || "",
        description: data.description || "",
        geometrics_outline: outline || ""
      });

      setDrawVersion(v => v + 1);
      setTimeout(() => fitMapToBoundary(outline), 600);
      
    } catch (error) {
      // UPDATED: Use showToast for error
      showToast("error", "Failed to load location data");
    }
  };

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  const onPlaceChanged = () => {
    const place = autocompleteRef.current?.getPlace();
    if (place?.geometry && mapRef.current) {
      if (place.geometry.viewport) {
        mapRef.current.fitBounds(place.geometry.viewport);
      } else {
        mapRef.current.panTo(place.geometry.location);
        mapRef.current.setZoom(15);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (id) {
        await api.put(`/top-locations/${id}`, formData);
        // UPDATED: Use showToast for success
        showToast("success", "Updated Successfully");
      } else {
        await api.post("/top-locations", formData);
        // UPDATED: Use showToast for success
        showToast("success", "Created Successfully");
      }
      navigate("/locations-top");
    } catch (error) {
      setLoading(false);
      // UPDATED: Use showToast for error
      showToast("error", "Save operation failed");
    }
  };

  return (
    <>
      <PageHeader 
        title={id ? "Edit Top Location" : "Add Top Location"} 
        subtitle="Geofence & Boundary Management" 
      />

      <div className="card radius-12 border-0 shadow-none bg-base">
        <div className="card-body p-24">
          <form onSubmit={handleSubmit}>
            <div className="row g-4">
              
              {/* Left Column: Form Controls */}
              <div className="col-lg-4">
                <div className="mb-24">
                  <label className="form-label fw-semibold text-secondary-light">Location Name</label>
                  <input
                    type="text"
                    className="form-control radius-8"
                    placeholder="Enter location name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>

                <div className="mb-24">
                  <label className="form-label fw-semibold text-secondary-light">Description</label>
                  <textarea
                    className="form-control radius-8"
                    rows="4"
                    placeholder="Describe this area..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </div>

                <div className="mb-24">
                  <label className="form-label fw-semibold text-secondary-light">Boundary Data (JSON Preview)</label>
                  <textarea
                    className="form-control radius-8 bg-neutral-50 text-secondary-light"
                    readOnly
                    style={{ height: 260, fontFamily: "monospace", fontSize: 11 }}
                    value={formData.geometrics_outline}
                  />
                  <div className="mt-8 d-flex align-items-center gap-2 text-xs text-secondary-light">
                    <Icon icon="lucide:info" className="text-primary-600" />
                    <span>Auto-updated via map drawing tools.</span>
                  </div>
                </div>
              </div>

              {/* Right Column: Interactive Map */}
              <div className="col-lg-8">
                {isLoaded ? (
                  <div className="position-relative">
                    <div className="icon-field mb-12">
                      <span className="icon">
                        <Icon icon="solar:magnifer-linear" />
                      </span>
                      <Autocomplete
                        onLoad={(a) => (autocompleteRef.current = a)}
                        onPlaceChanged={onPlaceChanged}
                      >
                        <input 
                          className="form-control radius-8 ps-40" 
                          placeholder="Search for a city or address..." 
                        />
                      </Autocomplete>
                    </div>

                    <div className="border radius-12 overflow-hidden shadow-sm">
                      <GoogleMap
                        mapContainerStyle={MAP_CONTAINER_STYLE}
                        center={DEFAULT_CENTER}
                        zoom={11}
                        onLoad={onMapLoad}
                        options={{
                            mapTypeControl: true,
                            streetViewControl: false,
                            fullscreenControl: true,
                        }}
                      >
                        <DrawTools
                          key={drawVersion}
                          data={formData.geometrics_outline}
                          onChange={(json) =>
                            setFormData((p) => ({
                              ...p,
                              geometrics_outline: json
                            }))
                          }
                        />
                      </GoogleMap>
                    </div>
                  </div>
                ) : (
                  <div 
                    className="w-100 radius-12 bg-neutral-100 d-flex align-items-center justify-content-center flex-column" 
                    style={{ height: "550px", border: "2px dashed #e3e6ef" }}
                  >
                    <Icon icon="line-md:loading-twotone-loop" className="text-primary-600 text-4xl mb-12" />
                    <p className="fw-medium text-secondary-light">Loading Geofence Engine...</p>
                  </div>
                )}
              </div>

              {/* Form Action Buttons */}
              <div className="col-12 d-flex align-items-center justify-content-end gap-3 mt-12">
                <button
                  type="button"
                  className="btn bg-danger-focus text-danger-main px-24 py-11 radius-8 fw-semibold border-0"
                  onClick={() => navigate("/locations-top")}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={loading}
                  className="btn btn-primary-600 px-32 py-11 radius-8 fw-semibold d-flex align-items-center gap-2"
                >
                  {loading ? (
                    <Icon icon="line-md:loading-twotone-loop" />
                  ) : (
                    <Icon icon="lucide:check-circle" />
                  )}
                  {id ? "Update Boundary" : "Save Geofence"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default LocationsTopFormPage;